import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { PencilIcon, SearchIcon, TrashIcon } from "lucide-react";
import { EditProductDialog } from "./EditProductDialog";
import { AuditCell } from "./AuditCell";
import { ConfirmActionDialog } from "./ConfirmActionDialog";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { cn } from "@/lib/utils";

interface ProductRow {
  id: string;
  organisationId: string;
  name: string;
  description: string | null;
  unitPrice: number;
  currency: string;
  type: string;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
}

interface ProductSearchHit {
  id: string;
  name: string;
  description: string | null;
  score: number;
}

interface ProductsTableProps {
  organisationId: string;
}

export function ProductsTable({ organisationId }: ProductsTableProps) {
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [editing, setEditing] = useState<ProductRow | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ProductRow | null>(null);

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, 400);
  const [hits, setHits] = useState<ProductSearchHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  const pagination = useCursorPagination({ initialLimit: 20 });
  const { cursor, limit, setPage, reset } = pagination;

  // Reset the cursor during render, not in an effect: an effect would send the stale cursor once, and a
  // cursor only means something within one organisation's list.
  const [cursorOrganisation, setCursorOrganisation] = useState(organisationId);
  if (cursorOrganisation !== organisationId) {
    setCursorOrganisation(organisationId);
    reset();
  }

  // Only the newest fetch may write: page size stays enabled mid-flight, so an older answer can land last.
  const requestId = useRef(0);

  const fetchProducts = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await api.GET("/v1/products", {
      params: { query: { Cursor: cursor, Limit: limit } },
      headers: { "x-dolfin-organisation-id": organisationId },
    });

    if (id !== requestId.current) return;

    if (apiError) {
      setError("Failed to load products.");
    } else {
      setProducts(((data as any)?.data as ProductRow[]) ?? []);
      setPage({
        nextCursor: (data as any)?.meta?.cursors?.next,
        previousCursor: (data as any)?.meta?.cursors?.previous,
      });
    }
    setLoading(false);
  }, [organisationId, cursor, limit, setPage]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    const q = debouncedQuery.trim();
    if (!q) {
      setHits([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    setSearching(true);
    (async () => {
      const { data, error: apiError } = await api.GET("/v1/products/search", {
        params: { query: { q, limit: 10 } },
        headers: { "x-dolfin-organisation-id": organisationId },
      });
      if (cancelled) return;
      if (!apiError && data) {
        const sorted = [...(data as ProductSearchHit[])].sort(
          (a, b) => Number(b.score ?? 0) - Number(a.score ?? 0),
        );
        setHits(sorted);
      } else {
        setHits([]);
      }
      setSearching(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, organisationId]);

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  // A hit is usually outside the loaded page, so fall back to the detail page.
  function handleSelectHit(id: string) {
    setShowResults(false);
    const row = rowRefs.current.get(id);
    if (!row) {
      navigate(`/products/${id}?organisationId=${organisationId}`);
      return;
    }
    row.scrollIntoView({ behavior: "smooth", block: "center" });
    setHighlightedId(id);
    window.setTimeout(() => {
      setHighlightedId((cur) => (cur === id ? null : cur));
    }, 2000);
  }

  async function handleDelete(id: string) {
    setDeleting(id);
    const { error: apiError } = await api.DELETE("/v1/products/{id}", {
      params: { path: { id } },
      headers: { "x-dolfin-organisation-id": organisationId },
    });

    if (apiError) {
      setError("Failed to delete product. It may be referenced by invoices.");
    } else {
      setProducts((prev) => prev.filter((p) => p.id !== id));
    }
    setDeleting(null);
    setDeleteTarget(null);
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">Products</h2>
          {loading && <Spinner className="size-5" />}
        </div>
        <div ref={searchRef} className="relative w-72">
          <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2" />
          <Input
            type="search"
            placeholder="Search products..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setShowResults(true);
            }}
            onFocus={() => setShowResults(true)}
            className="pl-8"
          />
          {showResults && debouncedQuery.trim() && (
            <div className="bg-popover text-popover-foreground absolute top-full right-0 left-0 z-20 mt-1 max-h-80 overflow-auto rounded-md border shadow-md">
              {searching ? (
                <div className="text-muted-foreground flex items-center justify-center gap-2 p-3 text-sm">
                  <Spinner className="size-4" /> Searching…
                </div>
              ) : hits.length === 0 ? (
                <div className="text-muted-foreground p-3 text-sm">No matches.</div>
              ) : (
                <ul className="py-1">
                  {hits.map((hit) => (
                    <li key={hit.id}>
                      <button
                        type="button"
                        onClick={() => handleSelectHit(hit.id)}
                        className="hover:bg-accent hover:text-accent-foreground w-full px-3 py-2 text-left text-sm"
                      >
                        <div className="truncate font-medium">{hit.name}</div>
                        {hit.description && (
                          <div className="text-muted-foreground truncate text-xs">
                            {hit.description}
                          </div>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {error && <p className="text-destructive mb-4 text-sm">{error}</p>}

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Unit Price</TableHead>
              <TableHead>Currency</TableHead>
              <TableHead>Last modified</TableHead>
              <TableHead className="w-[100px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-muted-foreground h-24 text-center">
                  No products found.
                </TableCell>
              </TableRow>
            ) : (
              products.map((p) => (
                <TableRow
                  key={p.id}
                  ref={(el) => {
                    if (el) rowRefs.current.set(p.id, el);
                    else rowRefs.current.delete(p.id);
                  }}
                  className={cn(
                    "transition-colors duration-500",
                    highlightedId === p.id && "bg-accent",
                  )}
                >
                  <TableCell className="font-medium">
                    <Link
                      to={`/products/${p.id}?organisationId=${organisationId}`}
                      className="hover:underline"
                    >
                      {p.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Badge variant={p.type === "Service" ? "secondary" : "outline"}>
                      {p.type}
                    </Badge>
                  </TableCell>
                  <TableCell>{p.unitPrice}</TableCell>
                  <TableCell>{p.currency}</TableCell>
                  <TableCell>
                    <AuditCell
                      createdAt={p.createdAt}
                      createdBy={p.createdBy}
                      updatedAt={p.updatedAt}
                      updatedBy={p.updatedBy}
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        onClick={() => setEditing(p)}
                      >
                        <PencilIcon className="size-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        disabled={deleting === p.id}
                        onClick={() => setDeleteTarget(p)}
                      >
                        {deleting === p.id ? (
                          <Spinner className="size-4" />
                        ) : (
                          <TrashIcon className="size-4" />
                        )}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />

      <EditProductDialog
        organisationId={organisationId}
        product={editing}
        open={editing !== null}
        onOpenChange={(open) => { if (!open) setEditing(null); }}
        onUpdated={fetchProducts}
      />

      <ConfirmActionDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Delete this product?"
        description={
          <>Deleting <span className="font-medium">{deleteTarget?.name}</span> cannot be undone. If it's referenced by any invoices, the delete will fail.</>
        }
        confirmLabel="Delete"
        pending={!!deleting}
        onConfirm={() => { if (deleteTarget) handleDelete(deleteTarget.id); }}
      />
    </div>
  );
}
